import FullScreenLoading from "@/core/components/FullScreenLoading";
import { User } from "firebase/auth";
import React, { createContext, useContext, useState } from "react";
import { firebaseApp, firebaseAuth, firebaseDb } from "../../FirebaseConfig";

import { useSnackbar } from "@/core/contexts/SnackbarProvider";

import Equestrain from "@/lib/equestrain";
import { CustomTypes } from "@/shared-types/v1";
import { Club, FilterGroup, LibraryAsset, VideoAsset } from "@/shared-types/v2";

if (!process.env.REACT_APP_FUNCTIONS_ADDR) {
  throw new Error("REACT_APP_FUNCTIONS_ADDR not set");
}

const equestrain = new Equestrain({
  baseUrl: process.env.REACT_APP_FUNCTIONS_ADDR,
  firebaseAuth: firebaseAuth,
  firebaseDB: firebaseDb,
  firebaseApp: firebaseApp,
});

interface AuthContextInterface {
  isLoggingInOrOut: boolean;
  login: (email: string, password: string) => Promise<any>;
  logout: () => Promise<any>;
  user?: User;
  isLoggedIn: () => boolean;
  dataLoading: boolean;
  equestrain: Equestrain;
  // data related props
  /** legacy video assets */
  videoAssets: CustomTypes.Asset[];
  /** set legacy video assets */
  setVideoAssets: React.Dispatch<React.SetStateAction<CustomTypes.Asset[]>>;
  /** newer version of video assets */
  videos: VideoAsset[];
  /** set newer version of video assets */
  setVideos: React.Dispatch<React.SetStateAction<VideoAsset[]>>;
  filterGroups: FilterGroup[];
  setFilterGroups: React.Dispatch<React.SetStateAction<FilterGroup[]>>;
  setLibraryAssets: React.Dispatch<React.SetStateAction<LibraryAsset[]>>;
  libraryAssets: LibraryAsset[];
  categories: Club.Category[];
  setCategories: (categories: Club.Category[]) => void;
  users: any[];
  setUsers: React.Dispatch<React.SetStateAction<any[]>>;
  usersPageToken?: string;
  setUsersPageToken: React.Dispatch<React.SetStateAction<string | undefined>>;
}

export const AuthContext = createContext({} as AuthContextInterface);

type AuthProviderProps = {
  children?: React.ReactNode;
};

const AuthProvider = ({ children }: AuthProviderProps) => {
  //snackbar
  const snackbar = useSnackbar();
  // auth and loading states
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<User>();
  const [isLoggingInOrOut, setIsLoggingInOrOut] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  // other states
  const [videoAssets, setVideoAssets] = useState<CustomTypes.Asset[]>([]);
  const [videos, setVideos] = useState<VideoAsset[]>([]);
  // library filter groups
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([]);
  // library assets
  const [libraryAssets, setLibraryAssets] = useState<LibraryAsset[]>([]);
  // club categories
  const [categories, setCategories] = useState<Club.Category[]>([]);
  // users
  const [users, setUsers] = useState<any[]>([]);
  const [usersPageToken, setUsersPageToken] = useState<string | undefined>();

  /** pre-loads some data to reduce waiting time */
  const loadInitialData = async () => {
    // allow app some time to load data
    setTimeout(() => {
      if (!ready) {
        setReady(true);
      }
    }, 2500);
    if (!user) {
      return;
    }

    // there would be so many state updates that we don't want to trigger re-renders
    equestrain.Client.setSkipOnDataLoadingFunctions(true);

    // LOAD DATA START -->
    const fetchPromises: Array<Promise<any>> = [];

    // wait for video asset load first to avoid errors with other data
    if (videos.length === 0) {
      fetchPromises.push(
        equestrain.Client.getAllVideos().then((res) => {
          if (!res.error && res.data) {
            // setVideos(res.data.data);
            setVideos(res.data.data.filter((d) => !d.removed));
          }
          if (res.error) {
            snackbar.error(res.error.message);
          }
        })
      );
    }
    // load library filter groups
    if (filterGroups.length === 0) {
      fetchPromises.push(
        equestrain.Client.getFilterGroups().then((res) => {
          if (!res.error && res.data) {
            setFilterGroups(res.data);
          }
          if (res.error) {
            snackbar.error(res.error.message);
          }
        })
      );
    }
    // load library assets
    if (libraryAssets.length === 0) {
      fetchPromises.push(
        equestrain.Client.getLibraryAssets().then((res) => {
          if (!res.error && res.data) {
            setLibraryAssets(res.data);
          }
          if (res.error) {
            snackbar.error(res.error.message);
          }
        })
      );
    }
    if (categories.length === 0) {
      fetchPromises.push(
        equestrain.Client.getCategories().then((res) => {
          if (!res.error && res.data) {
            setCategories(res.data);
          }
          if (res.error) {
            snackbar.error(res.error.message);
          }
        })
      );
    }
    // load users
    if (users.length === 0) {
      fetchPromises.push(
        equestrain.Legacy.getUsers(500).then((res) => {
          if (!res.error && res.data) {
            setUsers(res.data.users);
            setUsersPageToken(res.data.pageToken);
          }
          if (res.error) {
            snackbar.error(res.error.message);
          }
        })
      );
    }
    // handle all fetch promises
    if (fetchPromises.length > 0) {
      try {
        await Promise.allSettled(fetchPromises);
      } catch (error) {
        console.error(error);
      }
    }
    equestrain.Client.setSkipOnDataLoadingFunctions(false);

    // <-- LOAD DATA END

    // if app is not ready by now, set it ready
    if (!ready) {
      setReady(true);
    }
  };

  React.useEffect(() => {
    return equestrain.Auth.onFirebaseAuthStateChanged((usr) => {
      if (usr) {
        setUser(usr);
      } else {
        setUser(undefined);
      }
    });
  }, []);

  React.useEffect(() => {
    equestrain.Auth.setOnDataLoadingStart(() => setIsLoggingInOrOut(true));
    equestrain.Auth.setOnDataLoadingEnd(() => setIsLoggingInOrOut(false));
    equestrain.Client.setOnDataLoadingStart(() => setDataLoading(true));
    equestrain.Client.setOnDataLoadingEnd(() => setDataLoading(false));
    equestrain.Legacy.setOnDataLoadingStart(() => setDataLoading(true));
    equestrain.Legacy.setOnDataLoadingEnd(() => setDataLoading(false));
  }, []);

  React.useEffect(() => {
    loadInitialData();
  // Deps intentionally omitted: loadInitialData is redefined on every render, so listing it would re-run this effect continuously.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: equestrain.Auth.isLoggedIn,
        isLoggingInOrOut,
        login: async (email, password) => {
          // set ready to false to show loading screen while user logs in
          setReady(false);
          return equestrain.Auth.login(email, password);
        },
        logout: equestrain.Auth.logout,
        dataLoading,
        user,
        equestrain,
        videoAssets,
        setVideoAssets,
        videos,
        setVideos,
        filterGroups,
        setFilterGroups,
        libraryAssets,
        setLibraryAssets,
        categories,
        setCategories,
        users,
        setUsers,
        usersPageToken,
        setUsersPageToken,
      }}
    >
      {ready ? children : <FullScreenLoading />}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}

export default AuthProvider;
